import { getBinaryDocument, useBinaryId } from '@ctw/shared/api/fhir/binaries';
import { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { ResourceTypeString } from '@ctw/shared/api/fhir/types';
import { Alert } from '@ctw/shared/components/alert';
import { Button } from '@ctw/shared/components/button';
import { ContentTypeIcon } from '@ctw/shared/components/icons/content-type-icon';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { useOpenSourceDocument } from '@ctw/shared/content/CCDA/use-open-source-document';
import type { FhirModelSubclassType } from '@ctw/shared/context/_internal/resource/resource-fetch';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { tw } from '@ctw/shared/utils/tailwind';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface ResponsiveSourceDocumentLinkProps<TResourceType extends ResourceTypeString> {
  binaryId?: string;
  model: FhirModelSubclassType<TResourceType>;
}

type UseDownloadBinary = {
  downloadBinaryById: (binaryId: string, filename: string) => void;
  isDownloading: boolean;
  error?: string;
};

export const base64toBlob = (data: string, type = 'application/pdf') => {
  const bytes = atob(data);
  let { length } = data;
  const out = new Uint8Array(length);

  while (length) {
    length -= 1;
    out[length] = bytes.charCodeAt(length);
  }

  return new Blob([out], { type });
};

export function useDownloadBinary(): UseDownloadBinary {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string>();
  const { fetchFromFqs } = useCTW();

  const downloadBinaryById = useCallback(
    (binaryId: string, filename: string) => {
      setIsDownloading(true);
      setError(undefined);

      const fetchAndDownloadBinary = async () => {
        let link: HTMLAnchorElement | null = null;
        try {
          const { binary } = await getBinaryDocument(fetchFromFqs, binaryId);
          // Create a new Blob from the decoded data
          let blob: Blob;
          if (binary.data === undefined) {
            // Refused oversized document (or any binary with no decoded data): the raw blob is
            // preserved, so download it as-is rather than producing an empty file from `?? ''`.
            blob = binary.blob;
          } else {
            try {
              // This method seems to work for pdfs but not for xml. So rather than
              // try to pre-determine which content types use which method, lets just
              // attempt to use the base64toBlob we took from FHIRPlace and fallback to
              // using the new Blob constructor directly.
              blob = base64toBlob(binary.data, binary.contentType);
            } catch (_error) {
              blob = new Blob([binary.data], { type: binary.contentType });
            }
          }

          // Create a link element, use it to download the file and then remove it
          link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;

          // Append link to the body
          document.body.appendChild(link);

          // Trigger download with click
          link.click();
        } catch (e) {
          setError(e instanceof Error ? e.message : `${e}`);
          // biome-ignore lint/suspicious/noConsole: intentional logging
          console.error(`Error downloading binary file: ${e}`);
        } finally {
          setIsDownloading(false);
          // Clean up and remove the link
          link?.parentNode?.removeChild(link);
        }
      };

      void fetchAndDownloadBinary();
    },
    [fetchFromFqs],
  );

  return { downloadBinaryById, isDownloading, error };
}

export const ResponsiveSourceDocumentLink = <TResourceType extends ResourceTypeString>({
  binaryId: externalBinaryId,
  model,
}: ResponsiveSourceDocumentLinkProps<TResourceType>) => {
  const { openSourceDocumentById } = useOpenSourceDocument();
  const { allowSmallBreakpointCCDAViewer } = useCTW();
  const { downloadBinaryById, error, isDownloading } = useDownloadBinary();
  const [width, setWidth] = useState(window.innerWidth);
  const binaryIdQuery = useBinaryId(model, true, externalBinaryId === undefined);
  const binaryId = useMemo(
    () => externalBinaryId ?? binaryIdQuery.data,
    [binaryIdQuery.data, externalBinaryId],
  );

  useEffect(() => {
    window.addEventListener('resize', () => setWidth(window.innerWidth));
  }, []);

  const isDownloadBtn = !allowSmallBreakpointCCDAViewer && width < 460;
  const downloadText = isDownloadBtn && !isDownloading ? 'Download' : '';

  if (binaryIdQuery.isLoading) {
    return <LoadingSpinner centered={true} iconClass="w-5 h-5" />;
  }

  if (!binaryId || isEmpty(binaryId)) {
    return <>{externalBinaryId}</>;
  }

  return (
    <div>
      {error && <Alert type="error" header={error} />}
      <Tooltip
        id="source-document-tooltip"
        content="View source document"
        place="top"
        enabled={!isDownloading}
      >
        <Button
          type="button"
          variant="link"
          className={tw`flex items-center gap-1 hover:opacity-80`}
          ariaLabel="View source document"
          disabled={isDownloading}
          telemetryContext={{ resourceType: model.resourceType, resourceId: model.id }}
          onClick={() => {
            if (binaryId) {
              if (isDownloadBtn) {
                downloadBinaryById(binaryId, model.resourceTypeTitle);
              } else {
                void openSourceDocumentById(binaryId);
              }
            }
          }}
        >
          <ContentTypeIcon
            contentType={
              model instanceof DocumentModel ? (model.contentType ?? 'unknown') : 'unknown'
            }
          />
          <span className={tw`block whitespace-nowrap`}>
            {!isDownloading ? (
              downloadText
            ) : (
              <LoadingSpinner className={tw`h-3`} message="Downloading" />
            )}
          </span>
        </Button>
      </Tooltip>
    </div>
  );
};
